/**
 * Product Card Carousel
 * Handles image browsing in product cards with area-based interaction:
 * - Bullet area (bottom ~25%): Carousel navigation (clicks/swipes)
 * - Upper/middle area: PDP navigation on click/tap
 */

class ProductCardCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.carousel = wrapper.querySelector('.product-card__carousel');
    this.track = wrapper.querySelector('.product-card__carousel-track');
    this.slides = wrapper.querySelectorAll('.product-card__carousel-slide');
    this.bullets = wrapper.querySelectorAll('.product-card__carousel-bullet');
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.productUrl = wrapper.dataset.productUrl;

    // Interaction zones
    this.bulletAreaThreshold = 0.25; // Bottom 25% is bullet area

    // Touch/swipe properties
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50;
    this.maxTapMovement = 10; // Max pixel movement to be considered a tap (not scroll)
    this.touchStartTime = 0;
    this.maxTapDuration = 300; // ms - distinguishes tap from swipe

    if (this.slides.length > 1) {
      this.init();
    } else {
      // Single image - just handle PDP navigation
      this.initSingleImageNavigation();
    }
  }

  /**
   * Check if click/touch is in the bullet control area (bottom portion)
   */
  isInBulletArea(clientY) {
    const rect = this.carousel.getBoundingClientRect();
    const relativeY = clientY - rect.top;
    const threshold = rect.height * (1 - this.bulletAreaThreshold);
    return relativeY >= threshold;
  }

  /**
   * Navigate to PDP
   */
  navigateToPDP() {
    if (this.productUrl) {
      window.location.href = this.productUrl;
    }
  }

  /**
   * Initialize for single image (no carousel)
   */
  initSingleImageNavigation() {
    if (!this.carousel) return;

    // Desktop: click navigates to PDP
    if (window.innerWidth >= 700) {
      this.carousel.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigateToPDP();
      });
    }

    // Mobile: only genuine tap (not scroll) navigates to PDP
    if (window.innerWidth < 700) {
      this.carousel.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
        this.touchStartTime = Date.now();
      }, { passive: true });

      this.carousel.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        const touchDuration = Date.now() - this.touchStartTime;

        const diffX = Math.abs(this.touchStartX - this.touchEndX);
        const diffY = Math.abs(this.touchStartY - this.touchEndY);

        // Only navigate if it's a genuine tap (minimal movement)
        const isGenuineTap = touchDuration < this.maxTapDuration &&
                            diffX <= this.maxTapMovement &&
                            diffY <= this.maxTapMovement;

        if (isGenuineTap) {
          e.preventDefault();
          this.navigateToPDP();
        }
        // If scrolling, do nothing and allow default scroll behavior
      });
    }
  }

  init() {
    // Bullet click navigation (desktop & mobile)
    this.bullets.forEach((bullet, index) => {
      bullet.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goToSlide(index);
      });
    });

    // Desktop click navigation
    if (window.innerWidth >= 700) {
      this.carousel.addEventListener('click', (e) => {
        // Don't interfere with bullet clicks
        if (e.target.closest('.product-card__carousel-bullet')) {
          return;
        }

        e.preventDefault();

        const isInBulletZone = this.isInBulletArea(e.clientY);

        if (isInBulletZone) {
          // Bullet area: carousel navigation (left/right)
          const rect = this.carousel.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const halfWidth = rect.width / 2;

          if (clickX < halfWidth) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        } else {
          // Upper/middle area: navigate to PDP
          this.navigateToPDP();
        }
      });
    }

    // Mobile touch/swipe navigation
    if (window.innerWidth < 700) {
      this.carousel.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
        this.touchStartTime = Date.now();
      }, { passive: true });

      this.carousel.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        const touchDuration = Date.now() - this.touchStartTime;

        const diffX = Math.abs(this.touchStartX - this.touchEndX);
        const diffY = Math.abs(this.touchStartY - this.touchEndY);

        const isInBulletZone = this.isInBulletArea(e.changedTouches[0].clientY);

        // Check if it's a horizontal swipe for carousel
        const isHorizontalSwipe = diffX > this.minSwipeDistance && diffX > diffY;

        // A genuine tap must have minimal movement in BOTH directions (not scrolling)
        const isGenuineTap = touchDuration < this.maxTapDuration &&
                            diffX <= this.maxTapMovement &&
                            diffY <= this.maxTapMovement;

        if (isHorizontalSwipe) {
          // Horizontal swipe anywhere on image: carousel navigation
          e.preventDefault();
          this.handleSwipe();
        } else if (!isInBulletZone && isGenuineTap) {
          // Upper/middle area genuine tap (not scroll): navigate to PDP
          e.preventDefault();
          this.navigateToPDP();
        } else if (isInBulletZone && isGenuineTap) {
          // Bullet area tap: do nothing (allow bullet button interaction)
          e.preventDefault();
        }
        // If it's a scroll gesture (diffY > maxTapMovement), do nothing and allow default scroll
      });
    }
  }

  handleSwipe() {
    const diffX = this.touchStartX - this.touchEndX;
    const diffY = this.touchStartY - this.touchEndY;

    // Only trigger horizontal swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > this.minSwipeDistance) {
      if (diffX > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - previous slide
        this.previousSlide();
      }
    }
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentIndex || index < 0 || index >= this.slides.length) {
      return;
    }

    this.isTransitioning = true;

    // Update slides
    this.slides[this.currentIndex].classList.remove('is-active');
    this.slides[index].classList.add('is-active');

    // Update bullets
    this.bullets[this.currentIndex].classList.remove('is-active');
    this.bullets[index].classList.add('is-active');

    this.currentIndex = index;

    setTimeout(() => {
      this.isTransitioning = false;
    }, 300); // Match CSS transition duration
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  previousSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }
}

// Initialize all product card carousels
function initProductCardCarousels() {
  // Only initialize on collection pages
  const isCollectionPage = document.body.classList.contains('collection-page');

  if (!isCollectionPage) {
    return;
  }

  const wrappers = document.querySelectorAll('.product-card__media-wrapper[data-product-url]');

  wrappers.forEach(wrapper => {
    // Skip if already initialized
    if (wrapper.hasAttribute('data-carousel-initialized')) {
      return;
    }

    // Check if product has carousel element
    const carousel = wrapper.querySelector('.product-card__carousel');
    if (!carousel) {
      return;
    }

    // Check if carousel has multiple images
    const slides = wrapper.querySelectorAll('.product-card__carousel-slide');

    // Initialize for both single and multiple images
    // The ProductCardCarousel constructor will handle the logic
    new ProductCardCarousel(wrapper);

    // Mark as initialized to prevent duplicate initialization
    wrapper.setAttribute('data-carousel-initialized', 'true');
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardCarousels);
} else {
  initProductCardCarousels();
}

// Re-initialize when new products are loaded (for infinite scroll, AJAX, etc.)
document.addEventListener('product-list:updated', initProductCardCarousels);

// Observer for dynamically loaded products (infinite scroll, AJAX loading)
const productListObserver = new MutationObserver((mutations) => {
  let shouldInit = false;

  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length > 0) {
      mutation.addedNodes.forEach((node) => {
        // Check if the added node is a product card or contains product cards
        if (node.nodeType === 1) { // Element node
          if (node.classList && node.classList.contains('product-card')) {
            shouldInit = true;
          } else if (node.querySelector && node.querySelector('.product-card')) {
            shouldInit = true;
          }
        }
      });
    }
  });

  if (shouldInit) {
    // Small delay to ensure images are in the DOM
    setTimeout(initProductCardCarousels, 100);
  }
});

// Start observing product lists
document.addEventListener('DOMContentLoaded', () => {
  const productLists = document.querySelectorAll('.product-list, [class*="product"]');
  productLists.forEach((list) => {
    productListObserver.observe(list, {
      childList: true,
      subtree: true
    });
  });
});

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCardCarousel;
}
